/// <amd-module name="TFS/DRITools/Components/Callgraph/ParserTab" />
import d3 = require("d3");
import * as React from "react";

// Office Fabric
import { Label } from "OfficeFabric/Label";

// Resources
import * as Resources from "TFS/DRITools/Resources";
import { TraceMethod, TraceParserResult } from "TFS/DRITools/util/TraceParser";

// VSS
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

export interface IParserTabProps extends IVssComponentProperties {
    data: TraceParserResult;
}

interface Wrapping {
    [key: string]: Method;
}

interface Method {
    elements: (HTMLTableRowElement | null)[];
    parentId: string | null;
}

class ParserTab extends VssComponent<IParserTabProps> {
    private wrapping: Wrapping = {};

    public constructor(props: IParserTabProps, context: IVssComponentContext) {
        super(props, context);
    }

    public render(): JSX.Element {
        const { data } = this.props;
        return (
            <div className="trace-parse">
                {data.roots.map((root: TraceMethod, index: number) => {
                    return (
                        <div key={index}>
                            <Label>{root.name}</Label>
                            <table>
                                <thead>
                                    <tr>
                                        <th className="table-time">{Resources.Callgraph_Time}</th>
                                        <th className="table-inc">{Resources.Callgraph_Inc}</th>
                                        <th>{Resources.Callgraph_Method}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {root.children.map((child: TraceMethod) => {
                                        return this.constructNode(child);
                                    })}
                                </tbody>
                            </table>
                            =======================
                        </div>
                    );
                })}
            </div>
        );
    }

    private constructNode(node: TraceMethod, tabCount: number = 0, parentId: string | null = null): (any[] | JSX.Element | null)[] | JSX.Element {
        const thisId: string = this.newGuid();
        if (node.isSproc) {
            return (
                <tr key={thisId}>
                    <td>{node.start}</td>
                    <td>{":"}</td>
                    <td>
                        <span style={{ paddingLeft: tabCount * 10 + 8 + "px" } as React.CSSProperties}>{node.name + node.data[0]}</span>
                    </td>
                </tr>
            );
        } else {
            this.wrapping[thisId] = {
                elements: [],
                parentId: parentId
            };
            return [
                <tr
                    key={thisId}
                    ref={row => this.wrapping[thisId].elements.push(row)}
                    onMouseEnter={(event: React.MouseEvent<HTMLTableRowElement>) => this.handleMouseEnter(event, thisId)}
                    onMouseLeave={event => this.handleMouseOut(event, thisId)}
                >
                    <td>{node.start}</td>
                    <td>{node.end !== -1 ? ":" : ":unbal"}</td>
                    <td>
                        <span style={{ paddingLeft: tabCount++ * 10 + (node.end !== -1 ? 8 : 0) + "px" } as React.CSSProperties}>{node.name}</span>
                    </td>
                </tr>,
                node.children.map((child: TraceMethod) => {
                    return this.constructNode(child, tabCount, thisId);
                }),
                node.end !== -1 ? (
                    <tr
                        key={"-" + thisId}
                        ref={row => this.wrapping[thisId].elements.push(row)}
                        onMouseEnter={(event: React.MouseEvent<HTMLTableRowElement>) => this.handleMouseEnter(event, thisId)}
                        onMouseLeave={event => this.handleMouseOut(event, thisId)}
                    >
                        <td>{node.start}</td>
                        <td>{":" + node.end}</td>
                        <td>
                            <span style={{ paddingLeft: --tabCount * 10 + 8 + "px" } as React.CSSProperties}>\{node.name}</span>
                        </td>
                    </tr>
                ) : null
            ];
        }
    }

    private handleMouseEnter(event: React.MouseEvent<HTMLTableRowElement>, className: string): void {
        const node: Method = this.wrapping[className];
        node.elements.forEach((element: HTMLTableRowElement) => {
            d3.select(element).attr("class", "child");
        });
        if (node.parentId) {
            this.wrapping[node.parentId].elements.forEach((element: HTMLTableRowElement) => {
                d3.select(element).attr("class", "parent");
            });
        }
    }

    private handleMouseOut(event: React.MouseEvent<HTMLTableRowElement>, className: string): void {
        const node: Method = this.wrapping[className];
        node.elements.forEach((element: HTMLTableRowElement) => {
            d3.select(element).attr("class", "notIn");
        });
        if (node.parentId) {
            this.wrapping[node.parentId].elements.forEach((element: HTMLTableRowElement) => {
                d3.select(element).attr("class", "notIn");
            });
        }
    }

    private newGuid(): string {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
            const r = (Math.random() * 16) | 0,
                v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}

export default ParserTab;
